/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: aiProfilesApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client for AI Profiles module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';

export interface AiProfileListResponse {
  data: AiProfile[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AiProfile {
  id: number;
  uuid: string;
  domain_id: number;
  provider_id?: number | string;
  provider_name?: string;
  provider_type?: string;
  type: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  voice_model?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  voice_config?: any;
  embeddings_model?: string;
  profile_name?: string;
  timeout_ms?: number;
  retry_attempts?: number;
  status: boolean;
  system_prompt?: string | null;
  created_at: string;
  created_by?: number;
  updated_at?: string;
  updated_by?: number;
  last_tested?: string;
}

export interface CreateAiProfileInput {
  domain_id: number;
  provider_id?: number | string;
  type: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  voice_config?: string;
  embeddings_model?: string;
  profile_name: string;
  timeout_ms?: number;
  retry_attempts?: number;
  status?: boolean;
  system_prompt?: string | null;
}

export interface UpdateAiProfileInput {
  provider_id?: number | string;
  type?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  voice_config?: string;
  embeddings_model?: string;
  profile_name?: string;
  timeout_ms?: number;
  retry_attempts?: number;
  status?: boolean;
  system_prompt?: string | null;
}

export interface ListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  type?: string;
  status?: string;
  domainId?: number;
}

/**
 * Get all AI profiles
 */
export async function getAiProfiles(params?: ListParams): Promise<AiProfileListResponse> {
  const queryParams = new URLSearchParams();

  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.type) queryParams.append('type', params.type);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.domainId != null) queryParams.append('domainId', params.domainId.toString());

  const response = await fetch(`${buildApiUrl('/api/ai/profiles')}?${queryParams}`, {
    method: 'GET',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const payload = await response.json();
  const data = Array.isArray(payload?.data) ? payload.data : [];
  const pagination = payload?.pagination ?? {
    page: params?.page ?? 1,
    limit: params?.limit ?? 10,
    total: data.length,
    totalPages: 1
  };

  return { data, pagination };
}

/**
 * Get AI profile by ID
 */
export async function getAiProfileById(id: string | number): Promise<{ data: AiProfile }> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/${id}`), {
    method: 'GET',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create new AI profile
 */
export async function createAiProfile(data: CreateAiProfileInput): Promise<{ data: AiProfile }> {
  const response = await fetch(buildApiUrl('/api/ai/profiles'), {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update AI profile
 */
export async function updateAiProfile(
  id: string | number,
  data: UpdateAiProfileInput
): Promise<{ data: AiProfile }> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete AI profile
 */
export async function deleteAiProfile(id: string | number): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }
}

/**
 * Test AI profile connection
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function testAiProfileConnection(id: string | number): Promise<{ success: boolean; message: string; data: any }> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/${id}/test`), {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get available models for a provider
 */
export async function getProviderModels(providerId: number | string): Promise<{ data: { provider_id: number | string; provider_name: string; provider_type: string; models: string[] } }> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/providers/${providerId}/models`), {
    method: 'GET',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get available models organized by type for a provider
 */
export async function getProviderModelsByType(providerId: number | string): Promise<{ data: { provider_id: number | string; provider_name: string; provider_type: string; models_by_type: Record<string, string[]>; total_models: number } }> {
  const response = await fetch(buildApiUrl(`/api/ai/profiles/providers/${providerId}/models-by-type`), {
    method: 'GET',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get available voices for TTS models
 */
export async function getProviderVoices(providerId: number | string, model: string): Promise<{ data: { provider_id: number | string; provider_name: string; provider_type: string; model: string; voices: string[] } }> {
  const queryParams = new URLSearchParams({ model });
  const response = await fetch(buildApiUrl(`/api/ai/profiles/providers/${providerId}/voices?${queryParams}`), {
    method: 'GET',
    headers: getApiHeaders()
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

